import Link from "next/link";
import { BaseButton } from "@/src/component/base/BaseButton";
import { useCMS } from "@/src/hook/provider";
import { FrontendTrackingService } from "@/src/service";
import type { PostTrackingLogInput } from "@/src/service";
import { StrapiComponentType } from "@/src/type";
import { BrowserHooks } from "@/src/hook";

interface ButtonLinkModuleProps {
  buttonLink: StrapiComponentType.Element.ButtonLink;
  tracking?: PostTrackingLogInput;
  className?: string;
  forceButton?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

function ButtonLinkModule({ buttonLink, tracking, className, forceButton, disabled, onClick }: ButtonLinkModuleProps) {
  const cms = useCMS();
  const externalLink = BrowserHooks.useLinkWithTrackingParams(buttonLink.href, buttonLink.forwardMarketingParams ?? false);

  /**
   * if the link provided is external do not use the CMS link generator
   */
  function getHref(): string {
    return buttonLink.isExternal ? externalLink : cms.constructLink(buttonLink.href);
  }

  /**
   * track the button click
   */
  function handleClick(e: any) {
    FrontendTrackingService.postTrackingLog(
      tracking ?? {
        action: "click",
        actionId: buttonLink.trackingEvent?.gaEventName ?? buttonLink.href,
        actionIdType: buttonLink.trackingEvent?.gaEventName ? "custom_event" : "href",
        actionMessage: `button: ${buttonLink.name}`,
        triggeredFrom: "ButtonLinkModule.tsx",
        pushTo: {
          hubspot: true,
          googleAnalytics: buttonLink.trackingEvent?.gaEventName,
          meta: buttonLink.trackingEvent?.fbEventName,
          googleAds: buttonLink.trackingEvent?.googleAdsConversion,
          googleAdsConversionLabel: buttonLink.trackingEvent?.googleAdsConversionLabel,
        },
      }
    );

    if (typeof onClick === "function") {
      onClick();
    }

    const link = getHref();
    const isAnchorLink = link.includes(window.location.pathname) && !link.includes("#");
    if (forceButton || !isAnchorLink) {
      e.preventDefault();
      e.stopPropagation();
      window.open(getHref(), buttonLink.target ?? "self");
    }
  }

  return (
    <BaseButton
      component={forceButton ? undefined : Link}
      href={forceButton ? undefined : getHref()}
      //@ts-ignore - this error is incorrect
      target={forceButton ? undefined : (buttonLink.target ?? "self")}
      title={buttonLink.label}
      color={buttonLink.color}
      variant={buttonLink.variant}
      size={buttonLink.size ?? "medium"}
      sx={{
        width: buttonLink.width ? `${buttonLink.width}px` : undefined,
      }}
      aria-label={buttonLink.label}
      onClick={handleClick}
      className={className}
      disabled={disabled}
      prefetch={forceButton ? undefined : false}
    >
      {buttonLink.label}
    </BaseButton>
  );
}

export default ButtonLinkModule;
